import type { IPipelineService, PipelineWriteInput } from './IPipelineService.interface'
import type { PipelineConfig, PipelineSummary } from '../../types/pipeline.types'
import { authHeaders } from '../../lib/apiClient'

// ── API base URL ──────────────────────────────────────────────────────────────
// VITE_API_BASE_URL is injected by Vite at build time.
// Falls back to localhost for local development.

const BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001'

// ── Typed fetch helper ────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()), ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}))
    const message = (body as { message?: string }).message ?? `API error ${res.status}`
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

// ── StridePipelineService ──────────────────────────────────────────────────

export class StridePipelineService implements IPipelineService {
  listPipelines(): Promise<PipelineSummary[]> {
    return apiFetch<PipelineSummary[]>('/api/pipelines')
  }

  getPipeline(id: string): Promise<PipelineConfig> {
    return apiFetch<PipelineConfig>(`/api/pipelines/${id}`)
  }

  createPipeline(input: PipelineWriteInput): Promise<PipelineConfig> {
    return apiFetch<PipelineConfig>('/api/pipelines', {
      method: 'POST',
      body:   JSON.stringify(input),
    })
  }

  updatePipeline(id: string, input: PipelineWriteInput): Promise<PipelineConfig> {
    return apiFetch<PipelineConfig>(`/api/pipelines/${id}`, {
      method: 'PUT',
      body:   JSON.stringify(input),
    })
  }

  deletePipeline(id: string): Promise<void> {
    return apiFetch<void>(`/api/pipelines/${id}`, { method: 'DELETE' })
  }
}
